import {
  Account, Audit,
  CommercialAccount,
  Device,
  Organisation, Role, SimCard, Subscription,
  SubscriptionOrder,
  SubscriptionsResponse,
  User,
} from '../types';
import { ApiBaseService, ApiV2Service } from './api.base.service';
import Transformation from '@/classes/transformation';
import store from '@/store/store';
import moment from 'moment';

export interface SearchAccountUsers {
  users: User[];
  columnFilters: any;
  total: number;
}

export class AccountService {
  private api: ApiBaseService;

  constructor() {
    this.api = ApiV2Service;
  }

  public async getAccounts(): Promise<CommercialAccount[]> {
    return this.api.get(`accounts`);
  }

  public async getUserAccounts(): Promise<CommercialAccount[]> {
    return this.api.get(`accounts/all`);
  }

  public async getCommercialAccounts(): Promise<CommercialAccount[]> {
    return this.api.get(`accounts`);
  }

  public async getAccountSummary(accountId: number): Promise<CommercialAccount> {
    return this.api.get(`accounts/${accountId}/summary`);
  }

  public async getAccountOrganisations(accountId: number): Promise<Organisation[]> {
    return this.api.get(`accounts/${accountId}/organisations`);
  }

  public async transferDevice(accountId: number, deviceId: number, body: { sourceOrganisationId: number; destinationOrganisationId: number }): Promise<{ success: string }> {
    return this.api.put(`accounts/${accountId}/devices/${deviceId}/transfer`, body);
  }

  public async transferDevices(accountId: number, body: { sourceOrganisationId: number; destinationOrganisationId: number, deviceIds: number[] }): Promise<{ job_id: string }> {
    return this.api.put(`accounts/${accountId}/devices/transfer`, body);
  }

  public async transferDetachedSubscription(accountId: number, body: { sourceOrganisationId: number; destinationOrganisationId: number, subscriptionIds: string[] }): Promise<{ success: string }> {
    return this.api.put(`accounts/${accountId}/subscriptions/transfer`, body);
  }

  public async transferDetachedSubscriptionsBulk(accountId: number, body: { destinationOrganisationId: number, subscriptionIds: string[] }): Promise<{ job_id: string }> {
    return this.api.put(`accounts/${accountId}/subscriptions/transfer/bulk`, body);
  }

  public async canTransferDevicesCheck(accountId: number, body: { sourceOrganisationId: number; destinationOrganisationId: number, deviceIds: number[] }): Promise<{ result: boolean; reason?: string }> {
    return this.api.post(`accounts/${accountId}/devices/canTransfer`, body);
  }


  public async canTransferSubscriptionsCheck(accountId: number, body: { sourceOrganisationId: number; destinationOrganisationId: number, subscriptionIds: string[] }): Promise<{ result: boolean; reason?: string }> {
    return this.api.post(`accounts/${accountId}/subscriptions/canTransfer`, body);
  }

  public async getAccountUsers(
    accountId: number,
    take: number = 0, skip: number = 0, sort: string = '',
    desc: boolean = false,
    filters: Record<string, Record<string, boolean>> = {},
    search: string | null = null,
    ): Promise<SearchAccountUsers> {
    let filtersString = '';

    const filterQuery = Transformation.filterMapToQueryString(filters);
    if (filterQuery) {
      filtersString += `&${filterQuery}`;
    }
    if (search) {
      filtersString += `&searchNames=${encodeURIComponent(search)}`;
    }
    return this.api.get(`accounts/${accountId}/users?take=${take}&skip=${skip}&sort=${sort}&desc=${desc}&includeColumnFilters=true${filtersString}`);
  }


  public async getAccountUserAudit(
    accountId: number,
    take: number = 0, skip: number = 0, sort: string = '',
    desc: boolean = true, filters: Record<string, Record<string, boolean>> = {},
    search: string | null = null,
    userId: number | null = null): Promise<any> {
    let filtersString = '';

    const filterQuery = Transformation.filterMapToQueryString(filters);
    if (filterQuery) {
      filtersString += `&${filterQuery}`;
    }
    if (search) {
      filtersString += `&search=${encodeURIComponent(search)}`;
    }
    if (userId) {
      filtersString += `&userId=${encodeURIComponent(userId)}`;
    }
    return this.api.get(`accounts/${accountId}/users/audit?take=${take}&skip=${skip}&sort=${sort}&desc=${desc}&includeColumnFilters=true${filtersString}`);
  }

  public async updateCommercialAccount(account: any): Promise<CommercialAccount> {
    return this.api.post(`accounts/${account.id}`, account);
  }

  public async createCommercialAccount(account: any): Promise<CommercialAccount> {
    return await this.api.put(`accounts`, account);
  }

  public async updateUserAccess(accountId: number, userId: number, body: { accountRoleIds: number[]; organisationsWithRoles: Array<{ organisationId: number; roleId: number }> }): Promise<CommercialAccount> {
    return this.api.put(`accounts/${accountId}/users/${userId}`, body);
  }

  public async addAccountUser(accountId: number, body: { user_email: string; accountRoleIds: number[]; organisationsWithRoles: Array<{ organisationId: number; roleId: number }> }): Promise<{ success: string }> {
    return this.api.post(`accounts/${accountId}/users/add`, body);
  }

  public async removeAccountUsers(accountId: number, body: { userIds: number[] }): Promise<{ success: string }> {
    return this.api.put(`accounts/${accountId}/users`, body);
  }

  public async createOrganisation(accountId: number, payload: { name: string }): Promise<Organisation> {
    return this.api.post(`accounts/${accountId}/organisations`, payload);
  }

  public async updateOrganisation(accountId: number, organisationId: number, payload: { name: string }): Promise<Organisation> {
    return this.api.put(`accounts/${accountId}/organisations/${organisationId}`, payload);
  }

  public async getSimsForAccountSubscriptionOrder(accountId: number, subscriptionOrderId: number): Promise<SimCard[]> {
    return this.api.get(`accounts/${accountId}/sims/orders/${subscriptionOrderId}`);
  }

  public async getAccountSubscriptionOrders(
    accountId: number,
    showInactive: boolean = false,
    take: number = 0, skip: number = 0, sort: string | string[] = '',
    desc: boolean = false, filters: Record<string, Record<string, boolean>> = {},
    search: string | null = null): Promise<{ subscriptionOrders: SubscriptionOrder[]; columnFilters: any; total: number }> {
    let filtersString = '';

    const filterQuery = Transformation.filterMapToQueryString(filters);
    if (filterQuery) {
      filtersString += `&${filterQuery}`;
    }
    if (search) {
      filtersString += `&searchNames=${encodeURIComponent(search)}`;
    }
    return this.api.get(`accounts/${accountId}/subscriptions/orders?showInactive=${showInactive}&take=${take}&skip=${skip}&sort=${sort}&desc=${desc}&includeColumnFilters=true${filtersString}`);
  }

  public async getAccountSubscriptionProducts(accountId: number, orderIds: number[]) {
    return this.api.put(`accounts/${accountId}/subscriptions/products`, { subscriptionOrderIds: orderIds });
  }

  public async getAccountSubscriptionAudit(accountId: number, subscriptionId: string): Promise<Audit[]> {
    return this.api.get(`accounts/${accountId}/subscriptions/${subscriptionId}/audit`);
  }

  public async getAccountSubscriptions(
    accountId: number,
    take: number = 0, skip: number = 0, sort: string = '',
    desc: boolean = false,
    filters: Record<string, Record<string, boolean>> = {},
    timeFilters: { [key: string]: { start: Date | null; end: Date | null }} = {},
    params: {
      organisationId?: number;
      subscriptionOrderId?: number;
      projectId?: number;
      search?: string;
      expiresBetween?: { start: Date | string; end: Date | string };
      expired?: boolean;
      excludeExpired?: boolean;
      excludeInactive?: boolean;
      excludeActive?: boolean;
      excludeNotRenewing?: boolean;
      excludeDetached?: boolean;
      excludeAvailable?: boolean;
    },
  ): Promise<{ items: Subscription[], counts: { [key: string]: number }, total: number, columnFilters: any }> {
    let filtersString = '';
    const filterQuery = Transformation.filterMapToQueryString(filters);
    const timeFilterQuery = Transformation.timeFilterMapToQueryString(timeFilters);

    if (filterQuery) {
      filtersString += `&${filterQuery}`;
    }
    if (timeFilterQuery) {
      filtersString += `&${timeFilterQuery}`;
    }
    if (params.organisationId) {
      filtersString += `&organisationId=${params.organisationId.toString()}`;
    }
    if (params.projectId) {
      filtersString += `&projectId=${params.projectId.toString()}`;
    }
    if (params.expired) {
      filtersString += `&expired=true`;
    }
    if (params.expiresBetween) {
      filtersString += `&start=${moment(params.expiresBetween.start).format('YYYY-MM-DD')}`;
      filtersString += `&end=${moment(params.expiresBetween.end).format('YYYY-MM-DD')}`;
    }
    if (params.excludeExpired) {
      filtersString += `&excludeExpired=true`;
    }
    if (params.excludeInactive) {
      filtersString += `&excludeInactive=true`;
    }
    if (params.excludeActive) {
      filtersString += `&excludeActive=true`;
    }
    if (params.excludeAvailable) {
      filtersString += `&excludeAvailable=true`;
    }
    if (params.excludeNotRenewing) {
      filtersString += `&excludeNotRenewing=true`;
    }
    if (params.excludeDetached) {
      filtersString += `&excludeDetached=true`;
    }
    if (params.search) {
      filtersString += `&searchNames=${encodeURIComponent(params.search)}`;
    }
    // const queryString = `?${queryParams.toString()}`;
    return this.api.get(`accounts/${accountId}/subscriptions?take=${take}&skip=${skip}&sort=${sort}&desc=${desc}&includeColumnFilters=true${filtersString}`);
  }

  public async exportAccountSubscriptions(accountId: number, params: any): Promise<{ job_id: string }> {
    return this.api.post(`accounts/${accountId}/subscriptions/export`, params);
  }


  public async exportAccountUserAudits(accountId: number, params: any): Promise<{ job_id: string }> {
    return this.api.post(`accounts/${accountId}/users/audit/export`, params);
  }

  public async getRoles(accountId: number): Promise<{ roles: Role[] }> {
    return this.api.get(`accounts/${accountId}/roles`);
  }

  public async updateAccountSubscriptionOrder(accountId: number, subscriptionOrderId: number, body: any): Promise<void> {
    await this.api.put(`accounts/${accountId}/orders/${subscriptionOrderId}`, body);
  }

  public async toggleRenewal(accountId: number, subscriptionIds: string[], enabled: boolean): Promise<void> {
    await this.api.put(`accounts/${accountId}/subscriptions/autorenew?enabled=${enabled}`, { subscriptionIds });
  }

  public async terminateSubscriptions(accountId: number, subscriptionIds: string[]): Promise<void> {
    await this.api.put(`accounts/${accountId}/subscriptions/terminate`, { subscriptionIds });
  }

  public async resendPassword(accountId: number, userId: number): Promise<void> {
    await this.api.post(`accounts/${accountId}/users/${userId}/resend`, {});
  }

}

export default new AccountService();
